/**
 * @file    ProgramsManager.tsx
 * @scope   VIEW
 * @summary CPR "Manage programs" screen: header (calculation settings, partition filters, add /
 *          expand-all) plus the rollup's program cards and the Cancel / Apply footer.
 * @consumes useProgramsManager, ProgramsManagerHeader, ProgramItemCard
 * @produces `ProgramsManager` component
 * @sideEffects None directly — state lives in useProgramsManager (CPR context).
 * @notes Presenter half of the Container/Presenter split, mirroring the Enterprise FieldsManager
 *        (and reusing its styles). Data comes from the injected `ProgramCatalog`.
 */
import React from 'react';
import { useProgramsManager } from './useProgramsManager';
import ProgramsManagerHeader from './ProgramsManagerHeader';
import ProgramItemCard from './ProgramItemCard';
import { ProgramCatalog } from './useProgramCatalog';
import '../../../Enterprise/Views/FieldsManager/FieldsManager.scss';

interface ProgramsManagerProps {
  /** Rollup being edited. */
  rollupId: string | null;
  /** Programs / questions / filter fields (real GuardRails data). */
  catalog: ProgramCatalog;
  /** Keeps this program's card open on return from the filters view. */
  expandedProgramId?: string | null;
  /** Card tab to start with for `expandedProgramId` ("filters" after editing its filters). */
  defaultActiveTab?: 'details' | 'filters';
  /** Discards the edits made since the screen was opened. */
  onCancel: () => void;
  /** Keeps the edits and goes back. */
  onApply: () => void;
  /** Navigate to the partition filters editor. */
  onManagePartitionFilters?: () => void;
  /** Navigate to a program's filters editor — filterId=null means "Add". */
  onEditFilter?: (programId: string, filterId: string | null) => void;
  onAdvancedSettings?: (programId: string) => void;
}

const ProgramsManager: React.FC<ProgramsManagerProps> = ({
  rollupId,
  catalog,
  expandedProgramId = null,
  defaultActiveTab,
  onCancel,
  onApply,
  onManagePartitionFilters,
  onEditFilter,
  onAdvancedSettings,
}) => {
  const {
    metric,
    programs,
    programOptions,
    getQuestionOptions,
    getFilterFields,
    expandedIds,
    touchedIds,
    areAllExpanded,
    canApply,
    filterCountText,
    hasInvalidPrograms,
    hasInvalidPartitionFilters,
    handleCalculationTypeChange,
    handleAggregationTypeChange,
    aggregationType,
    handlePrecisionChange,
    handleAddProgram,
    handleRemoveProgram,
    handleProgramSelect,
    handleQuestionSelect,
    handleReorderPrograms,
    handleFilterRemove,
    handleAccordionToggle,
    handleProgramBlur,
    expandAll,
    collapseAll,
  } = useProgramsManager(rollupId, catalog, expandedProgramId);

  if (!metric) return null;

  return (
    <>
      <div className="manage-fields-content">
        <ProgramsManagerHeader
          rollupName={metric.displayName}
          calculationType={metric.calculationType}
          aggregationType={aggregationType}
          onAggregationTypeChange={handleAggregationTypeChange}
          precision={metric.precision}
          filterCountText={filterCountText}
          hasInvalidPrograms={hasInvalidPrograms}
          hasInvalidPartitionFilters={hasInvalidPartitionFilters}
          areAllExpanded={areAllExpanded}
          onCalculationTypeChange={handleCalculationTypeChange}
          onPrecisionChange={handlePrecisionChange}
          onAddProgram={handleAddProgram}
          onToggleExpandAll={(isExpanded) => (isExpanded ? expandAll() : collapseAll())}
          onManagePartitionFilters={onManagePartitionFilters}
        />

        {programs.length > 0 ? (
          <div className="fields-list">
            {programs.map((program, index) => (
              <ProgramItemCard
                key={program.id}
                program={program}
                index={index}
                isExpanded={expandedIds.has(program.id)}
                isTouched={touchedIds.has(program.id)}
                isLoading={catalog.isProgramLoading?.(program.dataCollectionGuid) ?? false}
                programOptions={programOptions}
                questionOptions={getQuestionOptions(program.dataCollectionGuid)}
                availableFilterFields={getFilterFields(program.dataCollectionGuid)}
                defaultActiveTab={expandedProgramId === program.id ? defaultActiveTab : undefined}
                onRemove={handleRemoveProgram}
                onProgramSelect={handleProgramSelect}
                onQuestionSelect={handleQuestionSelect}
                onReorder={handleReorderPrograms}
                onAccordionToggle={handleAccordionToggle}
                onBlur={handleProgramBlur}
                onAdvancedSettings={onAdvancedSettings}
                onEditFilter={(id, filterId) => onEditFilter?.(id, filterId)}
                onFilterRemove={handleFilterRemove}
              />
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <p>No programs added yet.</p>
          </div>
        )}
      </div>

      <div className="diy-drawer-controls">
        <button className="diy-button diy-button-enabled-inverse" onClick={onCancel}>
          Cancel
        </button>
        <button className="diy-button diy-button-enabled" onClick={onApply} disabled={!canApply}>
          Apply
        </button>
      </div>
    </>
  );
};

export default ProgramsManager;
